import { FC, useMemo, useState } from 'react';
import {
  LifecycleStatusTag,
  workspaceLifecycleStatus,
} from 'components/lifecycle-status-tag';
import { useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  InlineLoading,
  InlineNotification,
  Search,
} from '@carbon/react';
import { Close } from '@carbon/icons-react';
import { api } from 'lib/request/api';
import { activePreDrillWorkspaceHrefAtom } from 'components/pre-drill-workspace-launcher';
import type { PreDrillWorkspaceListResponse } from 'types/pre-drill-workspace';
import { parseLocalDateTime } from 'utils/dates';
import { formatNumber } from 'utils/number';
import styles from './workspaces-drawer.module.scss';

const GEOMETRY_WORKSPACES_KEY = 'wellbore-geometry-workspaces';

/**
 * The wellbore geometry workspaces built on any bore of this well, for the Well page's
 * View menu count and its drawer.
 *
 * Keyed on the well, so the menu's count and the drawer's list are one fetch. Held off
 * until it has a well, which is how the page defers the count to the menu opening.
 * @param {string} uwi The well, or undefined to hold the query.
 * @return {object} The query, selected down to its rows.
 */
export const useWellboreGeometryWorkspaces = (uwi: string | undefined) =>
  useQuery({
    queryKey: [GEOMETRY_WORKSPACES_KEY, 'for-well', uwi ?? ''],
    queryFn: ({ signal }) =>
      api
        .post('queries/wellbore_geometry_workspaces', {
          json: { query: { uwi10: [uwi] } },
          signal,
        })
        .json<PreDrillWorkspaceListResponse>(),
    enabled: Boolean(uwi),
    retry: 1,
    select: (data) => data.results,
  });

interface Props {
  uwi: string;
  open: boolean;
  onClose: () => void;
}

/**
 * The Well page's Wellbore Geometry Workspaces drawer: every geometry workspace on any
 * of this well's bores, each opening the workspace it names in place.
 *
 * The sibling of the Workspaces drawer, reached from the same View menu and drawn the
 * same way. Its rows read like the Explorer's View Source list, the other place these
 * workspaces appear, so one workspace looks the same in both.
 * @param {Props} props The well, and whether the drawer is open.
 * @return {JSX.Element | null} The drawer.
 */
export const GeometryWorkspacesDrawer: FC<Props> = ({ uwi, open, onClose }) => {
  // Opens over this page rather than routing off it, the way Wellbores' View Source
  // and the Well Workspaces drawer do, so Back returns the reader here.
  const openWorkspace = useSetAtom(activePreDrillWorkspaceHrefAtom);
  const [query, setQuery] = useState('');
  const { data, isPending, isError } = useWellboreGeometryWorkspaces(
    open ? uwi : undefined
  );
  const rows = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => row.name.toLowerCase().includes(needle));
  }, [rows, query]);

  if (!open) return null;

  const count = formatNumber({
    value: rows.length,
    minPrecision: 0,
    includeCommas: true,
  });

  return (
    <aside className={styles.drawer} aria-label="Wellbore geometry workspaces">
      <div className={styles.header}>
        <h3 className={styles.title}>Wellbore Geometry Workspaces</h3>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        {isPending ? <InlineLoading description="Loading workspaces…" /> : null}

        {isError ? (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle="Could not load this well's wellbore geometry workspaces."
            lowContrast
            hideCloseButton
          />
        ) : null}

        {!isPending && !isError ? (
          <>
            <p className={styles.count}>
              {count} {rows.length === 1 ? 'workspace' : 'workspaces'}
            </p>

            {rows.length === 0 ? (
              <p className={styles.empty}>
                No wellbore geometry workspaces for this well yet.
              </p>
            ) : (
              <Search
                size="sm"
                labelText="Search workspaces"
                placeholder="Search workspaces"
                value={query}
                onChange={(event) => setQuery(event.target.value ?? '')}
              />
            )}

            {rows.length > 0 && filtered.length === 0 ? (
              <p className={styles.empty}>No workspaces match.</p>
            ) : null}

            {filtered.map((row) => (
              <button
                type="button"
                key={row.id}
                className={styles.row}
                onClick={() => {
                  openWorkspace(row.href);
                  onClose();
                }}
              >
                <span className={styles.rowName}>{row.name}</span>
                <span className={styles.rowMeta}>
                  <LifecycleStatusTag
                    status={workspaceLifecycleStatus(row.promoted)}
                  />
                  {row.isDefault ? (
                    <LifecycleStatusTag status="default" />
                  ) : null}
                  {row.createdBy?.displayName
                    ? `Created by ${row.createdBy.displayName}`
                    : null}
                </span>
                <span className={styles.rowMeta}>
                  Created: {parseLocalDateTime(row.createdAt, true)} | Updated:{' '}
                  {parseLocalDateTime(row.updatedAt, true)}
                </span>
              </button>
            ))}
          </>
        ) : null}
      </div>
    </aside>
  );
};
